import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ExpandMore } from '@mui/icons-material';
import styled from '@emotion/styled';

import { CircularProgress, Snackbar, Alert, Accordion, AccordionSummary, AccordionDetails, Typography, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useErrorManager from "../../hooks/useErrorManager";

import GenericDetailsTable from '../Layout/Table/GenericDetailsTable';
import GatewaysTable from '../Gateways/GatewaysTable';
import TextLinkButton from "../Layout/TextLinkButton";

import { useBreadcrumb } from '../../contexts/BreadcrumbContext'; // Import breadcrumb context
import { useNavigate  } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';


const ButtonsContainer = styled(Box)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 16px;
  width: 100%;
`;


function LocationDetails() {
    const { locationId } = useParams();
    const [details, setDetails] = useState(null);

    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [loading, setLoading] = useState(true);

    const { setError, clearError, getError } = useErrorManager();
    const { showNotification } = useNotification();
    const { updateBreadcrumb } = useBreadcrumb(); // Use breadcrumb context to update breadcrumb
    
    const navigate = useNavigate();
    const axiosInstance = useAxiosPrivate();


    const detailsView = [
        { key: 'parentLocationName', label: 'Group'},
        { key: 'name', label: 'Name' }, // render: (value) => <Typography>{value}</Typography>
        { key: 'description', label: 'Description' },

        // { key: 'type', label: 'Type' },
        // { key: 'serial', label: 'Serial' },
        // { key: 'network', label: 'Network' },
        // { key: 'dmReadouts', label: 'DM Readouts' }
    ];



    useEffect(() => {
        const fetchLocationDetails = async () => {
            try {
                const response = await axiosInstance.get(`/locations/${locationId}/details`);
                setDetails(response.data[0]);
                clearError('fetch');
                setLoading(false);

                updateBreadcrumb([
                    { label: 'Manage Locations', link: '/locations' },
                    { label: response.data[0].name || 'Location', link: null }, // Dynamically set location name
                ]);


            } catch (error) {
                setError('fetch', error.message);
                setLoading(false);
            }
        };

        fetchLocationDetails();
    }, [locationId]);


    const handleLocationDelete = async () => {
        try {
            await axiosInstance.delete(`/locations/${locationId}`);
            clearError('delete');
            navigate(`/locations`, { state: { success: 'Location deleted successfully' }} )
            // Handle success (you may want to navigate or show a success message)
            setOpenConfirmDialog(false); // Close the dialog after successful deletion
        } catch (error) {
            setError('delete', error);
            showNotification('Failed to delete location','error')
            setOpenConfirmDialog(false); // Close the dialog even on failure
        }
    };

    const gwColumns = [
        { id: 'name', label: 'Name', filtered: true, render: (item) => <TextLinkButton action={() =>handleGatewaySelect(item.id)} text={item.name}/>},

        // { id: 'name', label: 'Name', filtered: true, render: (item) => <Button onClick={() => handleGatewaySelect(item.id)}>{item.name}</Button> },
        // { id: 'type', label: 'Type',  render: (item) => renderGatewayIcon(item.qnodeSupport,item.dmSupport) },
        // { id: 'location', label: 'Location', filtered: true, render: (item) => item.locationDetails.name },
        { id: 'serial', label: 'Serial', filtered: true },
        { id: 'network', label: 'Network', render: () => '-' },
        // { id: 'dmReadouts', label: 'DM Readouts', render: () => '-' }
    ];
    
    const handleGatewaySelect = async (gatewayId) => {
        navigate(`/gateways/${gatewayId}`);
    };


    return (
        loading ? (
                    <CircularProgress />
                ) : 
                getError('fetch') ? (
                    <Snackbar open={true} autoHideDuration={6000}>
                        <Alert severity="error">{getError('fetch')}</Alert>
                    </Snackbar>
                ) : 
                (
                    <>
                        <Accordion defaultExpanded={true}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h7">GENERAL INFORMATION</Typography>
                            </AccordionSummary>
                            <AccordionDetails>

                                <GenericDetailsTable detailsValue={details} detailsList={detailsView} />
                                <ButtonsContainer>
                                    <Button onClick={() => navigate(`/locations/edit/${locationId}`)} variant="contained" color="primary">Edit</Button>
                                    <Button onClick={() => setOpenConfirmDialog(true)} variant="contained" color="primary" style={{ marginLeft: '10px' }}>Delete</Button>
                                </ButtonsContainer>
                            </AccordionDetails>
                        </Accordion>

                        <Dialog
                            open={openConfirmDialog}
                            onClose={() => setOpenConfirmDialog(false)} // Close dialog when cancel is clicked
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    Are you sure you want to delete this location? This action cannot be undone.
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenConfirmDialog(false)} color="primary">
                                    Cancel
                                </Button>
                                <Button onClick={handleLocationDelete} color="primary" autoFocus>
                                    Confirm
                                </Button>
                            </DialogActions>
                        </Dialog>

{/* 
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h7">GENERAL INFORMATION</Typography>
                            </AccordionSummary>
                            <AccordionDetails>

                                <GenericDetailsTable
                                    detailsValue={details}
                                    detailsList={detailsView} />
                            </AccordionDetails>
                        </Accordion> */}

                        <Accordion defaultExpanded={true}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h7">GATEWAYS</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <GatewaysTable
                                    listOptions={`/gateways/list/byLocation/${locationId}`}
                                    columns = {gwColumns}
                                />
                            </AccordionDetails>
                         </Accordion>
                    </>            
                )
    );
}

export default LocationDetails;
